import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { TdInput, TdSelect } from '../../../../components/inline-add-table/cells';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goodsReceiptsApi } from '../goodsReceiptApi';
import type {
  GoodsReceiptDetail,
  GoodsReceiptInspectionStatus,
  GoodsReceiptLine,
} from '../goodsReceiptTypes';
import {
  GOODS_RECEIPT_INSPECTION_OPTIONS,
  GOODS_RECEIPT_INSPECTION_STATUS,
  isGoodsReceiptVerdictAccepted,
} from '../goodsReceiptTypes';

/**
 * Record the verdict on ONE delivered line.
 *
 * <b>DEDICATED to the line the reader opened it for, and it carries no picker</b> — they chose
 * the line by choosing whose row to press. The line is stated as read-only fact at the top (the
 * Table Display View) and everything below it is answered.
 *
 * <b>A serialized line is decided PER SERIAL and the counts are DERIVED from those verdicts.</b>
 * Two answers to "how many passed" — a figure and a set of decisions — would be free to disagree,
 * so the quantity is not asked for at all there and the server refuses one.
 *
 * <b>NO AUTOGENERATE.</b> This is a VERDICT, not sample data: a generator that accepted or
 * rejected somebody's goods in one click is the one place the affordance would be actively
 * dangerous. It also takes `<ResetUserPasswordModal>`'s exemption — the accepted quantity arrives
 * already seeded, so the primary action enables on the first try with nothing to do.
 *
 * <b>THIS IS THE SECOND OF TWO INSPECTIONS.</b> The receiving clerk already recorded what they
 * found on the dock, and the accepted box is seeded from THAT rather than from what arrived — so
 * the commonest verdict, *the dock was right*, needs no typing. It is a default and not a link:
 * this figure is the one that decides stock, and it overtypes freely. The dock's own breakdown is
 * stated above as read-only fact and is never rewritten from here.
 */
export function InspectDeliveryLineModal({
  open,
  receiptId,
  receiptNumber,
  line,
  onClose,
  onSaved,
}: {
  open: boolean;
  receiptId: string;
  receiptNumber: string;
  line: GoodsReceiptLine;
  onClose: () => void;
  onSaved: (receipt: GoodsReceiptDetail) => void;
}) {
  const [accepted, setAccepted] = useState('');
  const [status, setStatus] = useState<GoodsReceiptInspectionStatus | null>(null);
  const [notes, setNotes] = useState('');

  /*
    ONE VERDICT PER SERIAL, where this held { accepted, reason }. The CHECKBOX is gone — product
    call, 05 Oct 2026 — and with it the pair that could contradict itself: a serial recorded as
    accepted while carrying a reason it failed.
  */
  const [verdicts, setVerdicts] = useState<Record<string, GoodsReceiptInspectionStatus>>({});

  /** PER ITEM — the third column, and its own stored value on the serial row. */
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    setConfirmed(false);
    setError(null);
    setNotes(line.inspectionNotes ?? '');
    setStatus(line.inspectionStatus ?? null);

    /*
      SEEDED from what the DOCK passed, not from what arrived — 01 Oct 2026, with the receiving
      breakdown. The receiving clerk has already set two units aside as damaged, so starting the
      official inspection at the full received quantity would ask the inspector to re-discover
      what the delivery already records, and the commonest verdict — "the dock was right" —
      would be the one that needed typing.

      It is a DEFAULT rather than a link: the inspector overtypes it freely, and their figure is
      what decides stock. On a clean delivery `quantityGood` IS the received quantity, so nothing
      changes for the ordinary case.
    */
    setAccepted(String(line.quantityAccepted ?? line.quantityGood));

    /*
      DEFAULTS TO Good Quality, which is what the ticked checkbox it replaced defaulted to. A
      verdict IS an assertion, so a default asserts something — but an inspector walking fifty
      serials of which two failed would otherwise set forty-eight of them by hand, and the form
      this replaced already made that call.
    */
    setVerdicts(
      Object.fromEntries(
        line.serials.map((s) => [
          s.id,
          s.inspectionStatus ?? GOODS_RECEIPT_INSPECTION_STATUS.goodQuality,
        ]),
      ),
    );

    setRemarks(Object.fromEntries(line.serials.map((s) => [s.id, s.inspectionNotes ?? ''])));
  }, [open, line]);

  const acceptedCount = line.tracksSerial
    ? line.serials.filter((s) => {
        const verdict = verdicts[s.id];

        return verdict !== undefined && isGoodsReceiptVerdictAccepted(verdict);
      }).length
    : Number(accepted || 0);

  const rejectedCount = line.quantityReceived - acceptedCount;

  const quantityValid =
    line.tracksSerial ||
    (accepted.trim() !== '' &&
      Number.isInteger(Number(accepted)) &&
      Number(accepted) >= 0 &&
      Number(accepted) <= line.quantityReceived);

  /*
    A REJECTION OWES A FAILING VERDICT, and Good Quality is refused as firmly as a missing one:
    it is the member that says nothing failed. On a SERIALIZED line there is nothing left to
    check — every serial carries exactly one verdict by construction, which is the whole of what
    collapsing the checkbox and the reason into one control bought.

    Checked again on the server, where the rule lives; this is what stops the reader meeting it
    as a refusal.
  */
  const verdictValid =
    line.tracksSerial ||
    rejectedCount === 0 ||
    (status !== null && !isGoodsReceiptVerdictAccepted(status));

  const canSave = quantityValid && verdictValid && confirmed && !saving;

  const missing = !quantityValid
    ? `Enter how many of the ${line.quantityReceived} received passed inspection.`
    : !verdictValid
      ? 'Say why the rejected stock failed.'
      : 'Tick the box to confirm.';

  async function save() {
    setSaving(true);
    setError(null);

    try {
      const saved = await goodsReceiptsApi.inspectLine(receiptId, line.id, {
        // REFUSED on a serialized line, where the per-serial verdicts decide it.
        quantityAccepted: line.tracksSerial ? null : Number(accepted),

        // REFUSED on a serialized line, where the per-serial verdicts decide it.
        inspectionStatus: line.tracksSerial ? null : status,
        inspectionNotes: notes.trim() || null,
        serials: line.tracksSerial
          ? line.serials.map((s) => ({
              serialId: s.id,

              // ONE FIELD, where this sent a boolean and a string that could contradict it.
              inspectionStatus:
                verdicts[s.id] ?? GOODS_RECEIPT_INSPECTION_STATUS.goodQuality,
              inspectionNotes: (remarks[s.id] ?? '').trim() || null,
            }))
          : [],
      });

      onSaved(saved);
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('The inspection could not be saved.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'inspect-line' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Inspect ${line.sku}`}
      size="2xl"
      id="inspect-line-modal"
      confirm={{
        id: 'inspect-line-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="inspect-line-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Tooltip disabledReason={canSave ? null : missing} block={false}>
            <Button
              id="inspect-line-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The line as a read-only FACT — the reader is being shown this, not asked it. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Receipt', value: receiptNumber, mono: true },
            { label: 'Item', value: `${line.itemName} — ${line.sku}` },
            { label: 'Received', value: `${line.quantityReceived} ${line.unitCode}`, mono: true },
            { label: 'Ordered', value: `${line.quantityOrdered} ${line.unitCode}`, mono: true },

            /*
              WHAT THE DOCK FOUND, stated as fact rather than asked for again — this is the FIRST
              of the two inspections and the inspector is the second. It is what the accepted box
              below is seeded from, so stating it is what makes that seed explicable rather than
              a number that appeared.

              Drawn ONLY where the dock found something: a row reading "none" on every clean
              delivery is a line of chrome the reader learns to skip, and `<PreviewTable>` already
              renders an em dash for a null, which here would say the dock had not looked.
            */
            {
              label: 'Found on receipt',
              value:
                line.quantityUnusable > 0
                  ? [
                      line.quantityExpired > 0 ? `${line.quantityExpired} expired` : null,
                      line.quantityDamaged > 0 ? `${line.quantityDamaged} damaged` : null,
                      line.quantityWrongItem > 0 ? `${line.quantityWrongItem} wrong item` : null,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : null,
            },
            { label: 'Store', value: line.storageFacilityName },
            { label: 'Batch', value: line.batchNumber, mono: true },
            {
              label: 'Expiry',
              value: line.expiryDate ? formatDateOnly(line.expiryDate) : null,
              mono: true,
            },
            {
              label: 'Unit cost',
              value: line.unitCost == null ? null : formatMoney(line.unitCost),
              mono: true,
            },
          ]}
        />

        {line.tracksSerial ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Serials ({acceptedCount} of {line.serials.length} accepted)
            </p>

            <p className="text-xs text-slate-500">
              Each accepted serial becomes its own stock unit the moment this inspection is
              submitted. A rejected one never does, is in no stock figure, and is tagged for
              return to the supplier.
            </p>

            {/*
              A TABLE, with one column per question — product call, 05 Oct 2026, in the reader's
              own words: *this should be table based, with columns: serial, status, remarks (per
              item).* The list of rows it replaced carried the serial and a select with no header
              over either, so nothing on screen said what the control was for.

              NOT an <InlineAddTable>: its rows come from the serials recorded at delivery, so a
              `+` could add nothing and a trash icon would offer to delete a serial somebody
              counted off the box. It is the call the receive wizard's own line grid already
              takes, and what it borrows from that component is the CELLS.
            */}
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full table-fixed border-collapse">
                <thead>
                  <tr className="border-b border-b-slate-300 bg-slate-100 text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                    <th className="w-[34%] px-2 py-1.5 text-left">Serial</th>
                    <th className="w-[26%] border-l border-slate-200 px-2 py-1.5 text-left">
                      Status
                    </th>
                    <th className="border-l border-slate-200 px-2 py-1.5 text-left">Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {line.serials.map((serial, index) => {
                    const verdict =
                      verdicts[serial.id] ?? GOODS_RECEIPT_INSPECTION_STATUS.goodQuality;

                    return (
                      <tr key={serial.id} className="group border-t border-slate-200 align-middle">
                        <td className="truncate px-2 py-1 font-mono text-xs text-slate-600">
                          {serial.serialNumber}
                        </td>

                        <TdSelect
                          id={`inspect-line-serials.${index}.inspection-status`}
                          value={String(verdict)}
                          onChange={(next) =>
                            setVerdicts((current) => ({
                              ...current,
                              [serial.id]: Number(next) as GoodsReceiptInspectionStatus,
                            }))
                          }
                          options={GOODS_RECEIPT_INSPECTION_OPTIONS.map((o) => ({
                            value: String(o.value),
                            label: o.label,
                          }))}
                          ariaLabel={`Status of ${serial.serialNumber}`}
                          disabled={saving}
                        />

                        <TdInput
                          id={`inspect-line-serials.${index}.inspection-notes`}
                          value={remarks[serial.id] ?? ''}
                          onChange={(next) =>
                            setRemarks((current) => ({ ...current, [serial.id]: next }))
                          }
                          ariaLabel={`Remarks for ${serial.serialNumber}`}
                          placeholder="Optional"
                          disabled={saving}
                        />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <>
            <TextField
              id="inspect-line-quantity-accepted"
              label="Quantity accepted"
              required
              type="number"
              min={0}
              max={line.quantityReceived}
              value={accepted}
              onChange={(e) => setAccepted(e.target.value)}
              description="What passed and becomes stock. The rest is rejected, never becomes stock, and leaves the order line open for the supplier to re-deliver against."
              disabled={saving}
              error={error?.fieldError('quantityAccepted')}
            />

            {/*
              THE STATUS IS SHOWN OUTRIGHT, never conditionally. A control that appeared only
              once something was rejected moved every field under it as the reader typed, and the
              verdict is the thing this form exists to record — so it is always on screen.

              Its OPTIONS narrow instead: with nothing rejected the only honest verdict is Good
              Quality, so the picker states it and is disabled for STATE with the reason. The
              moment a quantity is rejected it offers the four failures and nothing else, which
              is exactly what the server accepts.
            */}
            <Select
              id="inspect-line-inspection-status"
              label="Item status"
              required
              value={rejectedCount === 0 ? String(GOODS_RECEIPT_INSPECTION_STATUS.goodQuality) : status === null ? '' : String(status)}
              onChange={(next) =>
                setStatus(next === '' ? null : (Number(next) as GoodsReceiptInspectionStatus))
              }
              options={(rejectedCount === 0
                ? GOODS_RECEIPT_INSPECTION_OPTIONS.filter((o) =>
                    isGoodsReceiptVerdictAccepted(o.value),
                  )
                : GOODS_RECEIPT_INSPECTION_OPTIONS.filter(
                    (o) => !isGoodsReceiptVerdictAccepted(o.value),
                  )
              ).map((o) => ({ value: String(o.value), label: o.label }))}
              sort="none"
              clearable={false}
              disabled={saving || rejectedCount === 0}
              description={
                rejectedCount === 0
                  ? 'Everything passed, so the verdict is Good Quality. Reject some of the quantity above to record a failure instead.'
                  : `Why the ${rejectedCount} rejected failed. They become no stock and are tagged for return to the supplier.`
              }
              error={error?.fieldError('inspectionStatus')}
            />
          </>
        )}

        <Textarea
          id="inspect-line-inspection-notes"
          label="Remarks"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything worth recording about the condition of this line…"
          disabled={saving}
        />

        <p className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          {rejectedCount > 0 ? (
            <>
              <b>
                {acceptedCount} of {line.quantityReceived} become stock when you submit.
              </b>{' '}
              The {rejectedCount} rejected never does — it is in no stock figure, and is tagged
              for return to the supplier. The order line stays open for it.
            </>
          ) : (
            <>
              <b>All {line.quantityReceived} become stock when you submit this.</b> The stock
              level, what is on hand and what is available all move immediately.
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}
